/**
 * Tiny diamond visualization for an in-play ball-landing point. Used inside
 * the inning-detail modal alongside each play that has hitData. Stripped-
 * down version of the main FieldDiamond — bases polygon + the dot, no
 * fielders / runners / animation. Coordinates are in the same 0..100
 * viewBox the main field uses (see `mapHit` in selectors.ts).
 */
import styles from './MiniHitDiamond.module.css';

interface MiniHitDiamondProps {
  x: number;
  y: number;
  trajectory?: string | null;
  hardness?: string | null;
  /** When set, wraps the SVG in a button that fires this callback. */
  onZoom?: () => void;
  /** When true, render at the larger "expanded" size (used inside ZoomModal). */
  expanded?: boolean;
}

// Match the BASE_POSITIONS in FieldDiamond so the dot lands in the right
// quadrant of this scaled-down view.
const BASES = {
  home: { x: 50, y: 70 },
  first: { x: 70, y: 50 },
  second: { x: 50, y: 30 },
  third: { x: 30, y: 50 },
};

function formatHitText(s: string | null | undefined): string | null {
  if (!s) return null;
  // GUMBO ships "ground_ball" / "fly_ball" / "line_drive" etc. Make those
  // human-readable: replace underscores with spaces and title-case the
  // first letter of each word.
  return s
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/(^|\s)\S/g, (c) => c.toUpperCase());
}

export default function MiniHitDiamond({
  x,
  y,
  trajectory,
  hardness,
  onZoom,
  expanded = false,
}: MiniHitDiamondProps): JSX.Element {
  const traj = formatHitText(trajectory);
  const hard = formatHitText(hardness);
  const captionBits = [traj, hard].filter(Boolean).join(' · ');
  const ariaLabel = captionBits || 'Ball landing';
  const svgClass = expanded ? `${styles.svg} ${styles.svgExpanded ?? ''}` : styles.svg;

  const inner = (
    <>
      <svg
        // viewBox extends above y=0 so the outfield arc (path "M 2 22 A 56 56 0 0 1 98 22 …")
        // — whose apex sits at y ≈ -6.8 — renders fully without clipping.
        viewBox="0 -10 100 110"
        className={svgClass}
        role="img"
        aria-label={ariaLabel}
      >
        <path
          d="M 2 22 A 56 56 0 0 1 98 22 L 50 70 Z"
          fill="var(--c-turf, #5a9b53)"
          opacity={0.42}
        />
        <line x1={50} y1={70} x2={2} y2={22} stroke="var(--c-ink-10)" strokeWidth={0.4} opacity={0.7} />
        <line x1={50} y1={70} x2={98} y2={22} stroke="var(--c-ink-10)" strokeWidth={0.4} opacity={0.7} />
        <polygon
          points={`${BASES.home.x},${BASES.home.y} ${BASES.first.x},${BASES.first.y} ${BASES.second.x},${BASES.second.y} ${BASES.third.x},${BASES.third.y}`}
          fill="var(--c-dirt, #c9a36a)"
          stroke="var(--c-dirt-stroke, #8a6f3e)"
          strokeWidth={0.5}
          opacity={0.85}
        />
        <circle cx={50} cy={50} r={3.5} fill="var(--c-dirt, #c9a36a)" stroke="var(--c-dirt-stroke, #8a6f3e)" strokeWidth={0.3} />
        {(['home', 'first', 'second', 'third'] as const).map((b) => (
          <rect
            key={b}
            x={BASES[b].x - 1.5}
            y={BASES[b].y - 1.5}
            width={3}
            height={3}
            fill="var(--c-ink-0, #fff)"
            stroke="var(--c-ink-50)"
            strokeWidth={0.3}
            transform={`rotate(45 ${BASES[b].x} ${BASES[b].y})`}
          />
        ))}
        <g data-testid="mini-hit-dot">
          <circle cx={x} cy={y} r={3.5} fill="var(--c-ball-landing-glow, rgba(250, 204, 21, 0.32))" />
          <circle
            cx={x}
            cy={y}
            r={1.6}
            fill="var(--c-ball-landing, #facc15)"
            stroke="var(--c-ink-100, #0c111c)"
            strokeWidth={0.3}
          />
        </g>
      </svg>
      {captionBits && (
        <figcaption
          className={styles.caption}
          title={
            hard
              ? 'Hardness rates how hard the ball was hit on contact: soft, medium, or hard (MLB GUMBO hitData.hardness).'
              : undefined
          }
        >
          {captionBits}
        </figcaption>
      )}
    </>
  );

  if (onZoom) {
    return (
      <button
        type="button"
        className={`${styles.wrap} ${styles.zoomable ?? ''}`}
        onClick={onZoom}
        aria-label={`Expand: ${ariaLabel}`}
        data-testid="mini-hit-diamond"
      >
        {inner}
      </button>
    );
  }

  return (
    <figure className={styles.wrap} data-testid="mini-hit-diamond">
      {inner}
    </figure>
  );
}
